import type { ReverbClientConfig } from './types.js';

export type ResolveReverbConfigInput = {
  apiBase: string;
  key?: string | null;
  host?: string | null;
  port?: number | string | null;
  scheme?: string | null;
};

function parsePort(value: number | string | null | undefined, fallback: number): number {
  if (value == null || value === '') return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/** `api.example.com` → `ws.api.example.com` */
export function deriveReverbHostFromApiBase(apiBase: string): string {
  try {
    const url = new URL(apiBase);
    const host = url.hostname;
    if (host.startsWith('ws.')) return host;
    if (host.startsWith('api.')) return host.replace(/^api\./, 'ws.');
    return `ws.${host}`;
  } catch {
    return 'localhost';
  }
}

export function resolveReverbConfig(
  input: ResolveReverbConfigInput,
): ReverbClientConfig | null {
  const key = input.key?.trim();
  if (!key) return null;

  const scheme = input.scheme === 'http' ? 'http' : 'https';
  const defaultPort = scheme === 'https' ? 443 : 80;

  return {
    key,
    host: input.host?.trim() || deriveReverbHostFromApiBase(input.apiBase),
    port: parsePort(input.port, defaultPort),
    scheme,
  };
}

/** Warn when API origin and explicit Reverb host look like different environments. */
export function detectReverbApiEnvMismatch(
  apiBase: string,
  reverbHost: string,
): string | null {
  const api = apiBase.toLowerCase();
  const ws = reverbHost.toLowerCase();

  const apiIsStage = api.includes('stage');
  const wsIsStage = ws.includes('stage');

  if (apiIsStage !== wsIsStage) {
    return `API (${apiBase}) and Reverb host (${reverbHost}) appear to be from different environments (stage vs prod). Use matching pair or Pusher returns 4009.`;
  }

  return null;
}
